import {
  Alert,
  ActionGroup,
  Button,
  Flex,
  FlexItem,
  Form,
  FormAlert,
  FormGroup,
  FormSelect,
  TextInput,
  FormSelectOption,
  PageSection,
  Title,
} from '@patternfly/react-core'
import {
  format,
  isBefore,
  isToday,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { PatientBirthSexId } from '@app/domain/patient/patient.interface'
import { hasContent } from '@app/lib/string'
import { ValidationStatusId } from '@app/lib/validators'
import type { AppState } from '@app/state'
import * as Patients from '@app/state/patients'
import { invalidDateText } from '@app/state/patients/messages'

export const NewPatientScreen = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const createPatientErrorMessage = useSelector<AppState, string>(
    (state) => state.patients.createPatientErrorMessage,
  )

  const [medicalRecordNumber, setMedicalRecordNumber] = useState<string>(
    undefined,
  )
  const [familyName, setFamilyName] = useState<string>(undefined)
  const [givenNames, setGivenNames] = useState<string>(undefined)
  const [birthSex, setBirthSex] = useState<string>(undefined)
  const [dateOfBirth, setDateOfBirth] = useState<string>(undefined)
  const [generalPractitioner, setGeneralPractitioner] = useState<string>(
    undefined,
  )
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [isFormSubmitted, setIfFormSubmitted] = useState(false)

  const [
    dateOfBirthValidationMessage,
    setDateOfBirthValidationMessage,
  ] = useState('')

  const handleTextOnBlur = useCallback(() => {
    setIsFormDirty(true)
  }, [])

  const validateIfFieldHasContent = useCallback((field) => {
    return hasContent(field)
      ? ValidationStatusId.Success
      : ValidationStatusId.Error
  }, [])

  const isDateOfBirthValid = useCallback((value: string): boolean => {
    if (!value) {
      setDateOfBirthValidationMessage('Date of Birth is required')
      return false
    }

    const parsedDate = parseISO(value)

    if (!isValid(parsedDate)) {
      setDateOfBirthValidationMessage(invalidDateText)
      return false
    }

    if (isBefore(parsedDate, startOfDay(new Date())) || isToday(parsedDate)) {
      setDateOfBirthValidationMessage('')
      return true
    } else {
      setDateOfBirthValidationMessage('Date must be in the past.')
      return false
    }
  }, [])

  const {
    dateOfBirthValidation,
    familyNameValidation,
    givenNameValidation,
    medicalRecordNumberValidation,
    birthSexValidation,
  } = useMemo(() => {
    if (isFormDirty) {
      return {
        dateOfBirthValidation: isDateOfBirthValid(dateOfBirth)
          ? ValidationStatusId.Success
          : ValidationStatusId.Error,
        familyNameValidation: validateIfFieldHasContent(familyName),
        givenNameValidation: validateIfFieldHasContent(givenNames),
        medicalRecordNumberValidation: validateIfFieldHasContent(
          medicalRecordNumber,
        ),
        birthSexValidation: validateIfFieldHasContent(birthSex),
      }
    } else {
      return {
        dateOfBirthValidation: ValidationStatusId.Default,
        familyNameValidation: ValidationStatusId.Default,
        givenNameValidation: ValidationStatusId.Default,
        medicalRecordNumberValidation: ValidationStatusId.Default,
        birthSexValidation: ValidationStatusId.Default,
      }
    }
  }, [
    isFormDirty,
    isDateOfBirthValid,
    dateOfBirth,
    validateIfFieldHasContent,
    familyName,
    givenNames,
    medicalRecordNumber,
    birthSex,
  ])

  const clearErrorMessage = useCallback(
    () => dispatch(Patients.actions.clearErrorMessage()),
    [dispatch],
  )

  const createNewPatient = useCallback(
    (newPatient: Patients.IApiNewPatient) => {
      clearErrorMessage(), dispatch(Patients.actions.createPatient(newPatient))
    },
    [clearErrorMessage, dispatch],
  )

  const isFormValid = useMemo(() => {
    return (
      hasContent(medicalRecordNumber) &&
      hasContent(familyName) &&
      hasContent(givenNames) &&
      hasContent(dateOfBirth) &&
      hasContent(birthSex) &&
      isDateOfBirthValid(dateOfBirth)
    )
  }, [
    birthSex,
    dateOfBirth,
    familyName,
    givenNames,
    isDateOfBirthValid,
    medicalRecordNumber,
  ])

  const handleSubmit = useCallback(() => {
    handleTextOnBlur()
    if (isFormValid) {
      const newPatientSubmission: Patients.IApiNewPatient = {
        medicalRecordNumber: medicalRecordNumber,
        familyName: familyName,
        givenNames: givenNames,
        dateOfBirth: dateOfBirth,
        generalPractitioner: generalPractitioner ? generalPractitioner : '',
        birthSex: birthSex,
      }
      setIfFormSubmitted(true)
      createNewPatient(newPatientSubmission)
    }
  }, [
    birthSex,
    createNewPatient,
    dateOfBirth,
    handleTextOnBlur,
    familyName,
    generalPractitioner,
    givenNames,
    isFormValid,
    medicalRecordNumber,
  ])

  return (
    <PageSection>
      <Title headingLevel="h1" size="xl">
        Create New Patient
      </Title>
      <Flex>
        <FlexItem>
          <div className="ci-c-form-basic">
            {createPatientErrorMessage !== '' && isFormSubmitted ? (
              <FormAlert>
                <Alert
                  variant="danger"
                  title={createPatientErrorMessage}
                  aria-live="polite"
                  isInline
                />
                <br />
              </FormAlert>
            ) : null}
            <Form isHorizontal>
              <FormGroup
                isRequired
                label="MRN"
                validated={medicalRecordNumberValidation}
                onBlur={handleTextOnBlur}
                fieldId="medical-record-number"
                helperTextInvalid="MRN is required"
              >
                <TextInput
                  value={medicalRecordNumber}
                  isRequired
                  type="text"
                  name="medical-record-number"
                  validated={medicalRecordNumberValidation}
                  onChange={setMedicalRecordNumber}
                />
              </FormGroup>
              <FormGroup
                isRequired
                label="Family Name"
                fieldId="family-name"
                validated={familyNameValidation}
                onBlur={handleTextOnBlur}
                helperTextInvalid="Family Name is required"
              >
                <TextInput
                  value={familyName}
                  isRequired
                  validated={familyNameValidation}
                  type="text"
                  name="family-name"
                  onChange={setFamilyName}
                />
              </FormGroup>
              <FormGroup
                isRequired
                label="Given Name(s)"
                fieldId="given-name"
                validated={givenNameValidation}
                onBlur={handleTextOnBlur}
                helperTextInvalid="Given Name is required"
              >
                <TextInput
                  value={givenNames}
                  isRequired
                  validated={givenNameValidation}
                  type="text"
                  name="given-name"
                  onChange={setGivenNames}
                />
              </FormGroup>
              <FormGroup
                label="Family Doctor"
                fieldId="general-practitioner"
                onBlur={handleTextOnBlur}
              >
                <TextInput
                  value={generalPractitioner}
                  type="text"
                  name="general-practitioner"
                  onChange={setGeneralPractitioner}
                />
              </FormGroup>
              <FormGroup
                isRequired
                fieldId="date-of-birth"
                label="DOB"
                validated={dateOfBirthValidation}
                onBlur={handleTextOnBlur}
                helperTextInvalid={dateOfBirthValidationMessage}
              >
                <TextInput
                  isRequired
                  type="date"
                  onChange={setDateOfBirth}
                  value={dateOfBirth}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </FormGroup>
              <FormGroup
                isRequired
                fieldId="birth-sex"
                label="Birth Sex"
                onBlur={handleTextOnBlur}
                validated={birthSexValidation}
                helperTextInvalid="Birth sex is required"
              >
                <FormSelect value={birthSex} isRequired onChange={setBirthSex}>
                  <FormSelectOption
                    key={0}
                    value={null}
                    label="Please Choose"
                  />
                  {Object.keys(PatientBirthSexId).map((key, idx) => (
                    <FormSelectOption
                      key={idx}
                      value={PatientBirthSexId[key]}
                      label={key}
                    />
                  ))}
                </FormSelect>
              </FormGroup>
              <ActionGroup style={{ float: 'right', marginTop: 10 }}>
                <Button
                  style={{ marginRight: 5 }}
                  variant="secondary"
                  onClick={() => history.go(-1)}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                  Create Patient
                </Button>
              </ActionGroup>
            </Form>
          </div>
        </FlexItem>
      </Flex>
    </PageSection>
  )
}
